// `configureSessionData` — the injected-config seam (design D7's named seam,
// carried by the first data family, D2). Packages are variant-agnostic and
// store-free (G1/F5): the app injects its React Query client singleton at
// bootstrap (`src/utils/session-data-wiring.ts`) so the package's exported
// COMMANDS (prefetch/invalidate free functions — design D6's law-6 shape) can
// address the same cache the app's hooks write. Hooks never need this seam —
// they read the client from React context like any consumer. Transport is NOT
// part of this seam either — `data/api` reads the app's configured axios
// singletons from the `@heidi/api/client` registry (ARCFE-805). Precedent:
// `@heidi/workspace`'s `configureWorkspaceData`.

import { type QueryClient } from '@tanstack/react-query';

import {
  type OfflineLocalSessionListItem,
  type OfflineSyncItem,
} from '../contracts/offline-session-list';

/**
 * The app state the composed `useQuerySessionById` branches on, resolved once
 * per render by the app (`src/hooks/useSessionAccessContext.ts`).
 *
 * Every member is a RESOLVED primitive. None of the underlying reads can happen
 * in this package: the router pathname, the `selectedReviewMemberAtom` cohort
 * read, two foreign `/data` doors (team general info · the user query), the
 * app's CASL ability and its `useIsAuthenticated()` verdict. Ruling 32
 * (`docs/monorepo-ui-floor-rulings/`) records the disposition and the reversal
 * of D3's split it carries.
 */
export interface SessionAccessContext {
  /** Haiku launch route — the session id in the URL is the EXTERNAL one. */
  isExternalLaunch: boolean;
  /**
   * The BRANCH read: a review member is selected, their team id has loaded,
   * and it is not the current user. Decides WHICH query the composed hook
   * returns.
   */
  isReviewingOtherUser: boolean;
  /**
   * The COHORT read (`useIsReviewingOtherUser()`), which gates whether the
   * personal session GET may fire at all.
   *
   * NOT the same expression as `isReviewingOtherUser` above, and deliberately
   * not collapsed into it: the branch read additionally requires a loaded TEAM
   * ID, this one requires a loaded USER RECORD. Both divergences are reachable
   * — team details still loading, or the user query still pending — and the
   * consequence of using the wrong one is a personal `GET /sessions/{id}` fired
   * for a reviewing user, which 403s and lands the id in `notFoundSessionIds`,
   * short-circuiting every downstream session query. Two reads, two members.
   */
  isReviewingOtherUserCohort: boolean;
  /** `teamDetails?.team.id ?? ''` — the team-member query's path segment. */
  teamId: string;
  /** `selectedReviewUser ?? ''` — the team-member query's path segment. */
  memberId: string;
  /** CASL: team-others-session-edit OR session-edit for this session. */
  hasSharedSessionEditPermission: boolean;
  /**
   * The app's `useIsAuthenticated().isAuthenticated` verdict — the gate on both
   * inner queries.
   *
   * NOT `!!token`. It is provider-aware: a persisted token with a settled
   * logged-out Kinde SDK, or a cache that has resolved to `unauthenticated`,
   * reads `false` here while the token is still in the store. Both inner hooks
   * moved off token presence onto this verdict; the composed read takes the
   * same one, from here, so all three gate identically.
   */
  isAuthenticated: boolean;
  /** The current user's kinde id — the team-member query's self-read guard. */
  currentUserId: string | undefined;
}

/**
 * What this device knows about the session list that the server does not:
 * which list cohort the user is in, whether the desktop can reach the backend,
 * and which offline recordings exist locally.
 *
 * Every member is a RESOLVED value. None of the underlying reads can happen in
 * this package: two are OpenFeature/Flagsmith flag reads, one wraps
 * `isTauri()` plus the native reachability atom, one selects over the app's
 * user query, and two are app atoms — and an app atom is never imported into a
 * package, it crosses as a prop or a port. They resolve app-side in
 * `src/hooks/useSessionListEnvironment.ts`.
 */
/**
 * Review-cohort primitives for the per-status session count. Every member is
 * resolved app-side: workspace identity, the review-member selection, and the
 * session-list allowlist are reads this package cannot perform.
 */
export interface SessionStatusCountReviewContext {
  /** `teamDetails?.team.id` — the team-member count's path segment. */
  teamId: string | undefined;
  /** The selected review member. Empty when nobody is selected. */
  selectedReviewMemberId: string | null | undefined;
  /** The current user's kinde id. */
  currentUserId: string | undefined;
  /** Support role, or the team's session-list review allowlist. */
  canReviewOtherUsersSessionList: boolean;
  /** A review member is selected and it is not the current user. */
  isReviewingOtherUser: boolean;
}

export interface SessionListEnvironment {
  /**
   * `release-scribe-enable-new-session-list`. The CHOKEPOINT input: the
   * combined read defaults its legacy `GET /sessions` OFF while the new list
   * is on, because React Query dedupes by key — one mounted subscriber with
   * `enabled: true` re-fires that request for every other subscriber. A
   * caller's explicit `enabled` still wins over it.
   */
  isNewSessionListEnabled: boolean;
  /**
   * `release-scribe-enable-offline-recording`. Gates whether the two arrays
   * below are unioned into the list at all.
   */
  isOfflineRecordingEnabled: boolean;
  /**
   * `useIsDesktopOnlineReady()` — true when it is safe for desktop code to hit
   * the backend. Web is always true. Drives the offline error/loading
   * suppression, so a false here turns a doomed fetch's error state into a
   * settled success rather than an error the list would render.
   */
  isDesktopOnlineReady: boolean;
  /**
   * `useUser((data) => data != null)` — a presence check, not an identity: the
   * request identifies the caller from the bearer token.
   */
  isUserLoaded: boolean;
  /** `offlineLocalSessionsAtom` — local-only shells with no queue row yet. */
  offlineLocalSessions: OfflineLocalSessionListItem[];
  /** `offlineSyncQueueAtom` — the durable sync queue. */
  offlineSyncQueue: OfflineSyncItem[];
}

export interface SessionDataConfig {
  /**
   * Returns the app's singleton `QueryClient` (web: `reactQueryClient`).
   * A getter — not the instance — so wiring order can never race client
   * construction.
   */
  getQueryClient: () => QueryClient;
  /**
   * Reactive auth token as a HOOK (S2, the workspace/commerce shape) — the
   * D5 document/context queries gate on it and must re-key on refresh.
   * Added at ARCFE-990; optional so pre-D5 wirings and doubles stay valid,
   * and the D5 hooks fail closed (disabled) when it is absent.
   */
  useAuthToken?: () => string | null | undefined;
  /**
   * The composed session read's app state as a HOOK, called unconditionally by
   * `useQuerySessionById` (ruling 32). See {@link SessionAccessContext} for
   * what each member is and why it cannot be read here.
   *
   * REQUIRED, and deliberately unlike `useAuthToken` above it. That member is
   * optional and the package calls it as `config.useAuthToken?.()` — an
   * *optionally-called hook*, safe only because the config is set once at
   * bootstrap and never changes, and a latent Rules-of-Hooks hazard if a wiring
   * ever went absent → present. The token can fail closed by disabling a query;
   * the SESSION READ HAS NO SAFE DEGRADED FORM — an absent member would
   * silently change WHICH session a reviewing user sees. Required removes the
   * hazard and the ambiguity together. (`useAuthToken` is left as it is: it has
   * pre-D5 wirings and doubles to keep valid, and fixing it is not this seam's
   * business.)
   *
   * Heals when nothing here is app-shaped any more — i.e. when the router
   * pathname, the review-member atom and the CASL ability all have doors. Every
   * member is an app read, so this seam is expected to outlive the others.
   */
  useSessionAccessContext: (sessionId: string) => SessionAccessContext;
  /**
   * The session list's device/cohort state as a HOOK, called unconditionally by
   * `useQueryCombinedSessions`. See {@link SessionListEnvironment} for what
   * each member is and why it cannot be read here.
   *
   * REQUIRED, for the same reason as `useSessionAccessContext` above and NOT
   * for the reason `useAuthToken` is optional. An optionally-called hook
   * (`config.useX?.()`) is a latent Rules-of-Hooks hazard the moment a wiring
   * goes absent → present, and this read has no safe degraded form in either
   * direction: absent flags would default the legacy `GET /sessions` back ON
   * underneath the new list — which re-fires it for every subscriber, since
   * React Query dedupes by key — and an absent `isDesktopOnlineReady` would
   * surface a doomed offline fetch's error to the list. Required removes the
   * hazard and the ambiguity together.
   *
   * Heals when nothing here is app-shaped any more — i.e. when the two flags
   * have a door and offline recording owns its own atoms in a package.
   */
  useSessionListEnvironment: () => SessionListEnvironment;
  /**
   * Subscribes the caller to this session's live transcript, so a re-record
   * that repopulates the transcript cache re-renders `useIsTranscriptDeleted`.
   * Returns nothing: the VALUE is read from the cache by
   * `areAllTranscriptsEmpty`; this member exists only for the subscription.
   *
   * A HOOK, and injected rather than reimplemented, because the app's
   * `useQueryLiveTranscriptBySessionId` is the capture boundary — it reads the
   * Redux timer, the capture store, the live-transcripts flag and the tab-share
   * state, none of which a headless data package may touch. Substituting a
   * snapshot read here would drop a fetch subscriber, which on some surfaces is
   * the only one.
   *
   * Optional (pre-existing wirings and test doubles stay valid) but ALWAYS
   * CALLED — the consumer resolves it to a module-scope no-op first rather than
   * writing `config.useLiveTranscriptSubscription?.()`, which would change the
   * hook count if a wiring ever went absent → present.
   */
  useLiveTranscriptSubscription?: (sessionId: string) => void;
  /**
   * The document brain transform (D5, ARCFE-990) — the app's `mapNewBrain`
   * free-user gating, injected because it composes app pricing/UI concerns
   * a data package may not read. Identity when absent. Used by BOTH the
   * document query and the module-scope `ensureQueryDocumentById` command
   * (which passes `isFreeUser` undefined, exactly as the legacy ensure did).
   */
  mapDocumentBrain?: (brain: unknown, isFreeUser?: boolean) => unknown;
  /**
   * Fetch-time read of the `release-session-fetch-timeout` rollout gate
   * (D3). A thunk, not a boolean: the original hub read the flag INSIDE the
   * queryFn, so a flag flip between render and a refetch must be observed —
   * a delegate-passed boolean would freeze it at render. Heals when the
   * rollout flag retires (the thunk becomes `() => true` and then deletes).
   */
  isSessionFetchTimeoutEnabled: () => boolean;
  /**
   * Classifies a fetch error as "session no longer exists" (drives 404
   * short-circuits + `notFoundSessionIds`). Injected because the predicate
   * is declared inside the capture surface
   * (`src/lib/SessionRecordingProvider/errors.ts` — the wiring's real import
   * site; ARCFE-711 kit zero-HTTP invariant) which a package cannot import.
   * Heals into a cross-package import when the capture kit ships its door.
   */
  isSessionNotFoundError: (error: unknown) => boolean;
  /**
   * Observability tag applied when a session detail fetch succeeds (web:
   * Datadog RUM `setUserProperty({ id: 'session_id' })`). Optional — the
   * package no-ops when absent. Heals into observability wiring when a
   * runtime telemetry door exists.
   */
  onSessionFetched?: (sessionId: string) => void;
  /**
   * `release-scribe-shared-patient-directory` reader as a HOOK (COLAB-1).
   * Injected because `packages/session` cannot import from another domain's
   * flags door — the backbone-ready rule accepts only a foreign `/contracts`
   * cross. The consuming site is `useQueryLinkedOwnSessionsByProfileId`,
   * which branches the flag-gated cutover to the v2.2 unified session list
   * ONLY on the own arm; the reviewing-other-user arm ignores it.
   *
   * OPTIONAL, and specifically for backwards compatibility with the many
   * mutation-test doubles that pre-date this member and never exercise the
   * linked-sessions read; those doubles stay valid. Callers of the linked-
   * sessions hook resolve the reference through a module-scope
   * always-returns-false fallback, so hook count is STABLE per app
   * instance regardless of wiring (no Rules-of-Hooks hazard within one
   * running React tree). Heals into a direct `@heidi/workspace/flags` import
   * when the shared-directory rollout retires and the flag descriptor moves
   * to a cross-domain contracts door.
   */
  useIsSharedPatientDirectoryEnabled?: () => boolean;
  /**
   * Injected emit sink for the operational signals raised across the linked-
   * sessions readers and the patient-delete foreign-session scan. Three events
   * share this sink because all three route to Datadog-only (the app owns the
   * routing decision; packages do not pick destinations):
   *
   * - `linked_sessions_profile_forbidden` — a 403 from the shared-directory
   *   pre-check on the flag-gated v2.2 linked-sessions reader
   * - `foreign_session_scan_truncated` — the COLAB-394 patient-delete guard
   *   read a page capped at `page_size`, so some linked sessions are unscanned
   * - `foreign_session_scan_failed` — the COLAB-394 guard's queryFn threw.
   *   Emitted once per settled error (keyed on `errorUpdatedAt`), not per
   *   RQ retry; the guard fails open on error until the BE check (COLAB-445)
   *   is the actual backstop, so this signal is how we see the gap
   *
   * `linked_sessions_truncated` was retired when cursor-following replaced the
   * single 200-item bulk fetch; it is no longer emitted.
   *
   * Injected because `packages/session/CLAUDE.md` requires analytics emits to
   * cross as a `Track…` callback — a direct `@heidi/runtime/analytics` import
   * inside `/data` violates the domain seam. Optional for the same double-
   * compatibility reason as the flag hook above.
   */
  trackLinkedSessionsSignal?: (
    event:
      | 'linked_sessions_profile_forbidden'
      | 'foreign_session_scan_truncated'
      | 'foreign_session_scan_failed',
    props: { flag_on: boolean; page_size?: number }
  ) => void;
  /**
   * Fetch-time ids of server sessions an offline delete still owes. A thunk:
   * the list reads it inside the query function, so a mid-flight queue change
   * is observed. Required — an absent read would leave deleted rows on screen.
   */
  readOfflinePendingServerDeleteIds: () => readonly string[];
  /**
   * Account-timezone parse of a session timestamp. Stays app-side because it
   * reads the account zone. Returns null when the input is missing or invalid.
   */
  parseTimeStampOrNull: (time: string | null | undefined, isUtc?: boolean) => Date | null;
  /**
   * The status-count query's review cohort, called unconditionally. Required
   * for the same reason as `useSessionAccessContext`: an absent member would
   * silently change which count a reviewing user sees.
   */
  useSessionStatusCountReviewContext: () => SessionStatusCountReviewContext;
}

let config: SessionDataConfig | null = null;

export const configureSessionData = (next: SessionDataConfig): void => {
  config = next;
};

/** Fail loudly on unconfigured use — never silently degrade. */
export const getSessionDataConfig = (): SessionDataConfig => {
  if (!config) {
    throw new Error(
      '[session/data] not configured — app bootstrap must call configureSessionData() ' +
        '(see src/utils/session-data-wiring.ts); tests exercising real session ' +
        'data commands must configure a test double first.'
    );
  }
  return config;
};
